/**
 * Task types available for assignments
 */
const TASK_TYPES = [
  'follow_up',
  'follow_up_call',
  'new_whatsapp',
  'follow_up_whatsapp',
  'follow_up_email',
  'new_sms',
  'follow_up_sms',
  'payment_verification',
  'other',
  'reply_missed_call',
  'reply_whatsapp',
  'reply_email',
  'reply_sms',
  // Arranque de expediente Accounts Receivable: el importador crea un assignment
  // de este tipo (a +2h) y el cron lo ejecuta SIN el modelo: aplica la
  // configuración de arranque de la empresa (docs/ar-arranque-plan.md).
  'init_ar',
  // Contacto telefónico de un expediente AR. Lo consume el marcador progresivo,
  // no un humano abriendo la tarea: ver CALLABLE_TASK_TYPES.
  'call_ar',
  // Un peldaño de la escalera de WhatsApp del arranque AR. Lo crea el propio
  // arranque, con el día que eligió la empresa, y lo ejecuta el código, no el
  // modelo. No lleva «whatsapp» en el nombre A PROPÓSITO: los tipos con esa
  // palabra pasan por el despachador de líneas de Maytapi, y esto va por Kapso.
  'template_ar',
  // Un SMS del arranque AR. Como `template_ar`: lo crea el arranque con el día
  // que eligió la empresa y lo manda el código. Ver docs/ar-sms-plan.md.
  'sms_ar',
];

/**
 * Task types que ejecuta el CÓDIGO del arranque AR, nunca el modelo.
 */
const AR_ARRANQUE_TASK_TYPES = ['init_ar', 'template_ar', 'sms_ar'];

/**
 * Task types que el AI Agent puede crear vía la tool `create_follow_up_assignment`.
 * Excluye los tipos "de sistema" del arranque AR (`init_ar`, `template_ar`), cuyo
 * alta es exclusiva del importador y del propio arranque: el agente no debe poder
 * emitir un contacto inicial sin dedup.
 */
const AGENT_CREATABLE_TASK_TYPES = TASK_TYPES.filter((t) => !AR_ARRANQUE_TASK_TYPES.includes(t));

/**
 * Lo que en AR no se crea para el agente (Edgar, 27/10/2026):
 *   · `follow_up_call`: el agente no llama. En AR llama el gestor que eligió la
 *     empresa (`companyarcalls.gestor_id`), y el ejecutor ya se la pasa a él;
 *   · `follow_up_sms`: en AR no hay conversaciones por SMS. El SMS del arranque
 *     es `sms_ar`, y lo manda el código.
 */
const AR_NOT_FOR_AGENT_TASK_TYPES = ['follow_up_call', 'follow_up_sms'];

/**
 * Task types que alimentan la cola del marcador progresivo.
 *
 * La pool NO es una colección aparte: es una consulta sobre assignments
 * `pending` de estos tipos, ordenada por score. Así el marcador hereda gratis lo
 * que ya existe (`next_retry_after`, `retry_count`, `plan_id`) y el motor de IA
 * puede meter trabajo en la cola creando assignments, sin API nueva.
 *
 * `reply_missed_call` entra porque un deudor que acaba de llamar y no fue
 * atendido es el mejor contacto disponible en ese momento: devolvérsela va antes
 * que cualquier expediente en frío.
 */
const CALLABLE_TASK_TYPES = ['follow_up_call', 'reply_missed_call', 'call_ar'];

/**
 * Assignment statuses
 */
const ASSIGNMENT_STATUSES = ['pending', 'in_progress', 'completed', 'cancelled', 'failed'];

/**
 * Assignment priorities
 */
const ASSIGNMENT_PRIORITIES = ['low', 'medium', 'high', 'urgent'];

/**
 * Map from debtorInteraction types to assignment task types
 * Only incoming interactions of these types will create assignments
 */
const INTERACTION_TO_ASSIGNMENT_MAP = {
  whatsapp_response: 'reply_whatsapp',
  email_response: 'reply_email',
  call_missed: 'reply_missed_call',
  // La respuesta a un SMS llega por el webhook de BulkGate. El tipo se llama
  // `sms_responded`, y no `sms_response`, porque así estaba ya en el modelo
  // y la ficha ya lo pintaba.
  sms_responded: 'reply_sms',
};

/**
 * De las tareas que nacen solas de una interaccion entrante, cuales van al
 * agente. El resto siguen naciendo sin asignar, para una persona.
 *
 * `reply_missed_call` se queda FUERA a proposito: el agente no marca. Es la
 * unica excepcion a «todo pasa por la IA» y es una decision de producto, no
 * una limitacion tecnica.
 *
 * Vive aqui y no en el hook porque el frontend tambien necesita saber que una
 * respuesta entrante ya tiene dueno: si no, la pantalla de Tareas invita a
 * asignarla a mano a alguien y se pisa con el agente.
 *
 * En la fase 4 esta lista se muda al panel del despachador —ver `D8` de
 * `docs/motor-whatsapp-en-claimup-plan.md`—, donde cada tipo de tarea dira si
 * pasa directo, si consume cupo y quien lo atiende. Hasta entonces, constante.
 */
const AUTO_ASSIGNED_TO_AGENT = ['reply_whatsapp', 'reply_email', 'reply_sms'];

/**
 * Spanish labels for assignment statuses
 */
const ASSIGNMENT_STATUS_LABELS = {
  pending: 'Pendiente',
  in_progress: 'En progreso',
  completed: 'Completado',
  cancelled: 'Cancelado',
  failed: 'Fallido',
};

/**
 * Spanish labels for assignment priorities
 */
const ASSIGNMENT_PRIORITY_LABELS = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  urgent: 'Urgente',
};

/**
 * Spanish labels for task types
 */
const TASK_TYPE_LABELS = {
  follow_up: 'Seguimiento',
  follow_up_call: 'Llamada de seguimiento',
  new_whatsapp: 'WhatsApp inicial',
  follow_up_whatsapp: 'WhatsApp de seguimiento',
  follow_up_email: 'Email de seguimiento',
  new_sms: 'SMS inicial',
  follow_up_sms: 'SMS de seguimiento',
  payment_verification: 'Verificación de pago',
  other: 'Otro',
  reply_missed_call: 'Responder llamada perdida',
  reply_whatsapp: 'Responder WhatsApp',
  reply_email: 'Responder email',
  reply_sms: 'Responder SMS',
  init_ar: 'Inicio gestión propia',
  template_ar: 'Plantilla WhatsApp gestión propia',
  sms_ar: 'SMS gestión propia',
  call_ar: 'Llamada gestión propia',
};

module.exports = {
  TASK_TYPES,
  AGENT_CREATABLE_TASK_TYPES,
  AR_NOT_FOR_AGENT_TASK_TYPES,
  AR_ARRANQUE_TASK_TYPES,
  CALLABLE_TASK_TYPES,
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_PRIORITIES,
  INTERACTION_TO_ASSIGNMENT_MAP,
  AUTO_ASSIGNED_TO_AGENT,
  ASSIGNMENT_STATUS_LABELS,
  ASSIGNMENT_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
};
